import React from 'react';
import { Palette } from 'lucide-react';
import ColorPicker from './ColorPicker';
import ImageUploader from './ImageUploader';
import { ThemeColorPalette } from '../../types';
import { DEFAULT_THEME_COLORS, mergeThemeColors } from '../../constants/themeColors';

interface BrandColorsProps {
  value?: ThemeColorPalette;
  onChange: (colors: ThemeColorPalette) => void;
  homeBgPreview?: string | null;
  onboardBgPreview?: string | null;
  onHomeBgChange?: (preview: string | null) => void;
  onOnboardBgChange?: (preview: string | null) => void;
  setValue?: any;
}

const editableKeys = ['PRIMARY', 'SECONDARY', 'ACCENT'] as const;

const BrandColors: React.FC<BrandColorsProps> = ({ 
  value = {}, 
  onChange, 
  homeBgPreview, 
  onboardBgPreview, 
  onHomeBgChange, 
  onOnboardBgChange, 
  setValue 
}) => {
  const brandColors = mergeThemeColors(value);

  const handleColorChange = (colorKey: keyof typeof brandColors, color: string) => {
    onChange({
      ...brandColors,
      [colorKey]: color,
    });
  };

  const colorConfigs = [
    {
      key: 'PRIMARY' as const,
      label: 'Primary Color',
      description: 'Main brand color used for headers and primary actions',
      color: brandColors.PRIMARY || DEFAULT_THEME_COLORS.PRIMARY,
    },
    {
      key: 'SECONDARY' as const,
      label: 'Secondary Color',
      description: 'Secondary brand color for accents and highlights',
      color: brandColors.SECONDARY || DEFAULT_THEME_COLORS.SECONDARY,
    },
    {
      key: 'ACCENT' as const,
      label: 'Accent Color',
      description: 'Accent color for buttons and interactive elements',
      color: brandColors.ACCENT || DEFAULT_THEME_COLORS.ACCENT,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2 mb-4">
        <Palette className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-medium text-gray-900">Brand Colors</h3>
      </div>
      
      <p className="text-sm text-gray-600 mb-6">
        Customize your app's brand colors. These will be used throughout the app interface.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {colorConfigs.map((config) => (
          <ColorPicker
            key={config.key}
            value={config.color}
            onChange={(color) => handleColorChange(config.key, color)}
            label={config.label}
            description={config.description}
            placeholder={DEFAULT_THEME_COLORS[config.key] || '#000000'}
          />
        ))}
      </div>

      {/* Color Preview */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Color Preview</h4>
        <div className="flex space-x-2">
          {editableKeys.map((key) => (
            <div key={key} className="text-center">
              <div
                className="w-8 h-8 rounded-lg border-2 border-white shadow-sm mb-1"
                style={{ backgroundColor: brandColors[key] || DEFAULT_THEME_COLORS[key] }}
              ></div>
              <span className="text-xs text-gray-600">{key}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Background Images */}
      <div className="space-y-6">
        <div className="flex items-center space-x-2 mb-4">
          <Palette className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-medium text-gray-900">Background Images</h3>
        </div>
        
        <p className="text-sm text-gray-600 mb-6">
          Upload custom background images for your app screens.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {onHomeBgChange && setValue && (
            <ImageUploader
              label="Home Background"
              fieldName={"homeBg" as any}
              preview={homeBgPreview || null}
              onPreviewChange={onHomeBgChange}
              setValue={setValue}
              inputId="home-bg-upload"
              description="Background image for the home screen, Required ratio 2:1"
            />
          )}
          
          {onOnboardBgChange && setValue && (
            <ImageUploader
              label="Onboard Background"
              fieldName={"onboardBg" as any}
              preview={onboardBgPreview || null}
              onPreviewChange={onOnboardBgChange}
              setValue={setValue}
              inputId="onboard-bg-upload"
              description="Background image for the language selection screen, Required ratio 9:16"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BrandColors;
